import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Box, Drawer, AppBar, Toolbar, Typography, List, ListItem,
  ListItemIcon, ListItemText, IconButton, useTheme, useMediaQuery,
  Divider, Avatar, Tooltip, Chip,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard,
  People,
  Tour,
  CalendarMonth,
  UploadFile,
  ReceiptLong,
  Settings,
  Link as LinkIcon,
  Extension,
  Help,
  BarChart,
  ListAlt,
  EventAvailable as EventAvailableIcon,
  AutoFixHigh as AutoFixHighIcon,
  Logout,
  Home,
  EventNote,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import UserProfileModal from './UserProfileModal';
import CreateTicketModal from './CreateTicketModal';

const drawerWidth = 260;

const StyledDrawer = styled(Drawer)(({ theme }) => ({
  '& .MuiDrawer-paper': {
    width: drawerWidth,
    boxSizing: 'border-box',
    backgroundColor: '#0f172a',
    color: '#94a3b8',
    overflowX: 'hidden',
  },
}));

const PAGE_TITLES: Record<string, { title: string; icon: React.ReactNode }> = {
  '/': { title: 'Дашборд', icon: <Dashboard /> },
  '/guides': { title: 'Экскурсоводы', icon: <People /> },
  '/tours': { title: 'Программы', icon: <Tour /> },
  '/extra-programs': { title: 'Доп. программы', icon: <Extension /> },
  '/calendar': { title: 'Календарь', icon: <CalendarMonth /> },
  '/upload': { title: 'Загрузка', icon: <UploadFile /> },
  '/summary': { title: 'Расчет', icon: <ReceiptLong /> },
  '/orders': { title: 'Заказы', icon: <ListAlt /> },
  '/guide-availability': { title: 'Доступность гидов', icon: <EventAvailableIcon /> },
  '/distribution': { title: 'Распределение экскурсий', icon: <AutoFixHighIcon /> },
  '/aliases': { title: 'Алиасы', icon: <LinkIcon /> },
  '/statistics': { title: 'Статистика', icon: <BarChart /> },
  '/periods': { title: 'Периоды', icon: <CalendarMonth /> },
  '/settings': { title: 'Настройки', icon: <Settings /> },
  '/help': { title: 'Помощь', icon: <Help /> },
};

const menuGroups = [
  [
    { text: 'Дашборд', path: '/', icon: <Dashboard /> },
    { text: 'Экскурсоводы', path: '/guides', icon: <People /> },
    { text: 'Программы', path: '/tours', icon: <Tour /> },
    { text: 'Доп. программы', path: '/extra-programs', icon: <Extension /> },
  ],
  [
    { text: 'Календарь', path: '/calendar', icon: <CalendarMonth /> },
    { text: 'Загрузка', path: '/upload', icon: <UploadFile /> },
    { text: 'Расчет', path: '/summary', icon: <ReceiptLong /> },
    { text: 'Заказы', path: '/orders', icon: <ListAlt /> },
    { text: 'Доступность гидов', path: '/guide-availability', icon: <EventAvailableIcon /> },
    { text: 'Распределение', path: '/distribution', icon: <AutoFixHighIcon /> },
  ],
  [
    { text: 'Алиасы', path: '/aliases', icon: <LinkIcon /> },
    { text: 'Статистика', path: '/statistics', icon: <BarChart /> },
    { text: 'Периоды', path: '/periods', icon: <CalendarMonth /> },
    { text: 'Настройки', path: '/settings', icon: <Settings /> },
    { text: 'Помощь', path: '/help', icon: <Help /> },
  ]
];

const Layout: React.FC<{ children: React.ReactNode, onLogout: () => void }> = ({ children, onLogout }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openProfile, setOpenProfile] = useState(false);
  const [openTicket, setOpenTicket] = useState(false);
  const [activePeriod, setActivePeriod] = useState<any>(null);
  const [toursCount, setToursCount] = useState<number>(0);
  const [guidesCount, setGuidesCount] = useState<number>(0);
  const [extraProgramsCount, setExtraProgramsCount] = useState<number>(0);
  const [calendarCount, setCalendarCount] = useState<number>(0);
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const fetchActivePeriod = () => {
    axios.get('/api/periods').then(res => {
      const active = res.data.find((p: any) => p.is_active === 1);
      if (active) setActivePeriod(active);
    }).catch(err => console.error('Ошибка загрузки периода:', err));
  };

  useEffect(() => {
    fetchActivePeriod();

    const handlePeriodActivated = () => fetchActivePeriod();
    window.addEventListener('periodActivated', handlePeriodActivated);

    axios.get('/api/tours').then(res => {
      if (Array.isArray(res.data)) setToursCount(res.data.length);
    }).catch(err => console.error('Ошибка загрузки программ:', err));

    axios.get('/api/guides').then(res => {
      if (Array.isArray(res.data)) {
        const activeGuides = res.data.filter((g: any) => g.is_active === 1 || g.is_active === true);
        setGuidesCount(activeGuides.length);
      }
    }).catch(err => console.error('Ошибка загрузки гидов:', err));

    axios.get('/api/extra-programs').then(res => {
      if (Array.isArray(res.data)) setExtraProgramsCount(res.data.length);
    }).catch(err => console.error('Ошибка загрузки доп. программ:', err));

    axios.get('/api/periods').then(res => {
      const active = res.data.find((p: any) => p.is_active === 1);
      if (active) return axios.get(`/api/calendar/period/${active.id}`);
      return null;
    }).then(res => {
      if (res && Array.isArray(res.data)) setCalendarCount(res.data.length);
    }).catch(err => console.error('Ошибка загрузки календаря:', err));

    return () => window.removeEventListener('periodActivated', handlePeriodActivated);
  }, [location.pathname]);

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);

  const drawer = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Toolbar sx={{ display: 'flex', alignItems: 'center', px: [2, 3] }}>
        <Typography variant="h6" noWrap component="div" sx={{ color: 'white', fontWeight: 'bold' }}>
          Экскурсоводы 2.0
        </Typography>
      </Toolbar>
      
      <Box sx={{ 
        flexGrow: 1, 
        overflowY: 'auto',
        '&::-webkit-scrollbar': { width: '0px' },
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
      }}>
        {menuGroups.map((group, groupIndex) => (
          <React.Fragment key={groupIndex}>
            <List sx={{ px: 2, py: 0 }}>
              {group.map((item) => {
                const isActive = location.pathname === item.path || (item.path !== '/' && location.pathname.startsWith(item.path));
                return (
                  <ListItem
                    key={item.text}
                    component={Link}
                    to={item.path}
                    onClick={() => { if (isMobile) setMobileOpen(false); }}
                    sx={{
                      my: 0.5,
                      borderRadius: 1,
                      color: isActive ? 'white' : '#94a3b8',
                      backgroundColor: isActive ? '#2563eb' : 'transparent',
                      '&:hover': { backgroundColor: isActive ? '#1d4ed8' : 'rgba(255,255,255,0.05)', color: 'white' },
                      py: 0.75,
                    }}
                  >
                    <ListItemIcon sx={{ color: 'inherit', minWidth: 36 }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText primary={item.text} slotProps={{ primary: { fontSize: '0.85rem', fontWeight: isActive ? 600 : 400, noWrap: true } }} />
                  </ListItem>
                );
              })}
            </List>
            {groupIndex < menuGroups.length - 1 && <Divider sx={{ bgcolor: 'rgba(255,255,255,0.1)', my: 1, mx: 2 }} />}
          </React.Fragment>
        ))}
      </Box>

      <Divider sx={{ bgcolor: 'rgba(255,255,255,0.1)' }} />
      <List sx={{ px: 2, py: 1 }}>
        <ListItem
          component="div"
          onClick={onLogout}
          sx={{
            borderRadius: 1, cursor: 'pointer', color: '#ef4444',
            '&:hover': { backgroundColor: 'rgba(239, 68, 68, 0.1)' },
          }}
        >
          <ListItemIcon sx={{ color: 'inherit', minWidth: 36 }}>
            <Logout />
          </ListItemIcon>
          <ListItemText primary="Выйти" slotProps={{ primary: { fontSize: '0.9rem', fontWeight: 500 } }} />
        </ListItem>
      </List>

      {/* --- Копирайт и ссылка на форму обратной связи --- */}
      <Box sx={{ px: 2, pb: 2, pt: 1 }}>
        <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 0.5, lineHeight: 1.4 }}>
          Разработка и поддержка проекта
          <br />
          Артюшин А.А.
        </Typography>
        <Box
          component="button"
          onClick={() => setOpenTicket(true)}
          sx={{
            background: 'none',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
            color: '#60a5fa',
            fontSize: '0.75rem',
            textDecoration: 'underline',
            fontFamily: 'inherit',
            '&:hover': { color: '#93c5fd' },
          }}
        >
          Создать заявку
        </Box>
      </Box>
      {/* --- Конец копирайта --- */}
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          bgcolor: 'white',
          color: 'text.primary',
          boxShadow: '0px 1px 3px rgba(0,0,0,0.1)',
          zIndex: theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              color="inherit"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2, display: { md: 'none' } }}
            >
              <MenuIcon />
            </IconButton>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ color: 'primary.main', display: 'flex' }}>
                {PAGE_TITLES[location.pathname]?.icon || <Dashboard />}
              </Box>
              <Typography variant="h5" noWrap component="div" sx={{ fontWeight: 700, color: 'text.primary', mr: 2 }}>
                {PAGE_TITLES[location.pathname]?.title || 'Guide'}
              </Typography>
              
              {activePeriod && (
                <Chip 
                  icon={<EventNote />}
                  label={`Активный период: ${activePeriod.month_name} ${activePeriod.year}`} 
                  color="primary" 
                  variant="outlined" 
                  size="small"
                  sx={{ fontWeight: 500 }}
                />
              )}

              {location.pathname === '/tours' && toursCount > 0 && (
                <Typography variant="body2" sx={{ color: 'text.secondary', ml: 2, fontWeight: 500 }}>
                  {toursCount} программ
                </Typography>
              )}

              {location.pathname === '/guides' && guidesCount > 0 && (
                <Typography variant="body2" sx={{ color: 'text.secondary', ml: 2, fontWeight: 500 }}>
                  {guidesCount} активных экскурсоводов
                </Typography>
              )}

              {location.pathname === '/extra-programs' && extraProgramsCount > 0 && (
                <Typography variant="body2" sx={{ color: 'text.secondary', ml: 2, fontWeight: 500 }}>
                  {extraProgramsCount} доп. программ
                </Typography>
              )}

              {location.pathname === '/calendar' && calendarCount > 0 && (
                <Typography variant="body2" sx={{ color: 'text.secondary', ml: 2, fontWeight: 500 }}>
                  {calendarCount} записей
                </Typography>
              )}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton size="small" onClick={() => navigate('/help')} title="Помощь">
              <Help fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={() => navigate('/settings')} title="Настройки">
              <Settings fontSize="small" />
            </IconButton>
            <Avatar 
              sx={{ width: 32, height: 32, ml: 1, bgcolor: 'primary.main', fontSize: '0.9rem', cursor: 'pointer' }} 
              onClick={() => setOpenProfile(true)}
            >
              А
            </Avatar>
          </Box>
        </Toolbar>
      </AppBar>

      <Box component="nav" sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}>
        <StyledDrawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{ display: { xs: 'block', md: 'none' } }}
        >
          {drawer}
        </StyledDrawer>
        <StyledDrawer
          variant="permanent"
          sx={{ display: { xs: 'none', md: 'block' } }}
          open
        >
          {drawer}
        </StyledDrawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, md: 3 },
          width: { md: `calc(100% - ${drawerWidth}px)` },
          mt: 8,
          bgcolor: '#f8fafc',
          minHeight: '100vh',
        }}
      >
        {children}
      </Box>
      <UserProfileModal open={openProfile} onClose={() => setOpenProfile(false)} onUpdate={() => window.dispatchEvent(new Event('profileUpdated'))} />
      <CreateTicketModal open={openTicket} onClose={() => setOpenTicket(false)} />
    </Box>
  );
};

export default Layout;
